import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditReportFolderMutation,
  useOrganizationsShortQuery,
  useReportFolderQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useEffect, useState } from 'react';
import { editFolderSchema } from '@views/pages/Reporting/components/EditReportFolderModal/schema';

interface IEditReportFolderFormShape {
  name: string;
  organization_id: string;
  isPrivate: boolean;
}

const initialValues: Partial<IEditReportFolderFormShape> = {
  name: '',
  organization_id: '',
  isPrivate: false,
};

export const useEditReportFolderModal = (
  handleClose: () => void,
  report_folder_id: string,
) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const formMethods = useForm<IEditReportFolderFormShape>({
    mode: 'onChange',
    resolver: yupResolver(editFolderSchema),
    defaultValues: initialValues,
  });

  const {
    reset,
    handleSubmit,
    getValues,
    formState: { isValid, isDirty },
  } = formMethods;

  const { data: organizations } = useOrganizationsShortQuery();
  const { data: folderDetails } = useReportFolderQuery(report_folder_id);

  useEffect(() => {
    if (folderDetails) {
      reset({
        name: folderDetails.name,
        organization_id: folderDetails.organization?.id,
        isPrivate: folderDetails.private,
      });
    }
  }, [folderDetails]);

  const { mutateAsync, isPending } =
    useEditReportFolderMutation(report_folder_id);

  const handleCloseAction = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      handleClose();
    }
  };

  const submitHandler = async (values: IEditReportFolderFormShape) => {
    try {
      await mutateAsync({
        name: values.name,
        private: values.isPrivate,
      });
      reset(values);
      handleClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    reset(getValues());
    setIsSaveChangesModalOpen(false);
  };

  return {
    formMethods,
    organizationOptions: (organizations || []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    onSubmit,
    isAddReportFolderPending: isPending,
    isDirty,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    handleCloseAction,
    containsAllowOthersToEdit: folderDetails?.contains_allow_others_to_edit,
  };
};
